/**
 * Adding a misc. line — a service charge, an accessory never worth its own catalogue entry,
 * anything the till needs to sell once. Deliberately a plain form, not the search-then-tap flow a
 * catalogue item gets: there is nothing to search for, and typing three fields is the honest
 * amount of friction for something that skips the catalogue entirely.
 */

import { useState } from 'react';

import { Button, Field, Sheet } from '@/shared/ui';

export type GenericLineSheetProps = {
  open: boolean;
  onAdd: (description: string, quantity: number, unitPriceKes: number) => void;
  onDismiss: () => void;
};

export function GenericLineSheet({ open, onAdd, onDismiss }: GenericLineSheetProps) {
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');

  const reset = () => {
    setDescription('');
    setQuantity('1');
    setPrice('');
  };

  const parsedQuantity = Math.trunc(Number(quantity));
  const parsedPrice = Math.trunc(Number(price));

  const valid =
    description.trim().length > 0 &&
    Number.isFinite(parsedQuantity) &&
    parsedQuantity > 0 &&
    Number.isFinite(parsedPrice) &&
    parsedPrice >= 0 &&
    price.trim().length > 0;

  return (
    <Sheet
      open={open}
      onDismiss={() => {
        reset();
        onDismiss();
      }}
      title="Service or other item"
    >
      <div className="flex flex-col gap-4">
        {/* Said before anything is typed: this skips stock, and the owner sees every one. "Misc.
            item" said neither, and read as jargon to the people it was for. */}
        <p className="text-sm text-content-muted">
          For something not in your product list — a repair, a service, a delivery charge. It does
          not come off stock, and the owner sees every one.
        </p>

        <Field
          label="What is it?"
          placeholder="e.g. Screen protector fitting"
          value={description}
          autoFocus
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />

        <Field
          label="Quantity"
          type="number"
          inputMode="numeric"
          min={1}
          value={quantity}
          onChange={(event) => {
            setQuantity(event.target.value);
          }}
        />

        <Field
          label="Price each (KES)"
          type="number"
          inputMode="decimal"
          min={0}
          placeholder="0"
          value={price}
          onChange={(event) => {
            setPrice(event.target.value);
          }}
        />

        <Button
          size="lg"
          fullWidth
          disabled={!valid}
          onClick={() => {
            onAdd(description.trim(), parsedQuantity, parsedPrice);
            reset();
          }}
        >
          Add to cart
        </Button>
      </div>
    </Sheet>
  );
}
